/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import {
  useAppMessage,
  useLocalSessionId,
  useParticipantProperty,
  useRoom,
} from '@daily-co/daily-react';
import { Arrow, Close } from '../../Tray/Icons';
import './Chat.css';
import api from '../../../api';

export default function Chat({ showChat, toggleChat }) {
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const localSessionId = useLocalSessionId();
  const username = useParticipantProperty(localSessionId, 'user_name');
  const userId = useParticipantProperty(localSessionId, 'user_id');
  const { name: roomName } = useRoom();

  const sendAppMessage = useAppMessage({
    onAppMessage: useCallback(
      (ev) =>
        setMessages((existingMessages) => [
          ...existingMessages,
          {
            msg: ev.data.msg,
            name: ev.data.name,
          },
        ]),
      [],
    ),
  });

  const sendMessage = useCallback(
    (message) => {
      /* Send the message to all participants in the chat - this does not include ourselves!
       * See https://docs.daily.co/reference/daily-js/events/participant-events#app-message
       */
      sendAppMessage(
        {
          msg: message,
          name: username || 'Guest',
        },
        '*',
      );

      /* Since we don't receive our own messages, we will set our message in the messages array.
       * This way _we_ can also see what we wrote.
       */
      setMessages([
        ...messages,
        {
          msg: message,
          name: username || 'Guest',
        },
      ]);

      api.sendRoomMessage(roomName, userId, localSessionId, username, message);
    },
    [messages, sendAppMessage, username],
  );

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return; // don't allow people to submit empty strings
    sendMessage(inputValue);
    setInputValue('');
  };

  useEffect(() => {
    async function fetchMessages() {
      setIsLoading(true);

      const previousMessages = await api.getRoomMessages(roomName);

      if (previousMessages?.messages) {
        setMessages(previousMessages.messages);
      }

      setIsLoading(false);
    }

    if (!messages.length) {
      fetchMessages();
    }
  }, []);

  return showChat ? (
    <aside className="chat">
      <div className="chat-header">
        <p className="chat-title">Chat</p>
        <button onClick={toggleChat} className="close-chat" type="button">
          <Close />
        </button>
      </div>

      <ul className="chat-messages">
        {isLoading ? (
          <div>Loading</div>
        ) : (
          <>
            {messages.map((message, index) => (
              <li key={`message-${index}`} className="chat-message">
                <span className="chat-message-author">{message?.name}</span>:{' '}
                <p className="chat-message-body">{message?.msg}</p>
              </li>
            ))}
          </>
        )}
      </ul>
      <div className="add-message">
        <form className="chat-form" onSubmit={handleSubmit}>
          <input
            className="chat-input"
            type="text"
            placeholder="Type your message here.."
            value={inputValue}
            onChange={handleChange}
          />
          <button type="submit" className="chat-submit-button">
            <Arrow />
          </button>
        </form>
      </div>
    </aside>
  ) : null;
}
